package org.teddapp.project.ui.finance

import org.teddapp.project.ui.LocalTeddFonts

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Expense_list
import com.tedd.db.Expense_list_item
import com.tedd.db.Finance_account
import com.tedd.db.Finance_category
import com.tedd.db.Finance_transaction
import kotlinx.coroutines.launch
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.LocalUseFullScreenForm
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddLogo
import org.teddapp.project.ui.TeddSnackbarHost
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.rememberTeddSnackbarState

// ──────────────────────────────────────────────────────────────────────
// Parties propres à la mise en page compacte : en-tête, bandeau du compte,
// barre d'onglets, bouton flottant, feuilles (changement de compte, ajout rapide).
// ──────────────────────────────────────────────────────────────────────

@Composable
internal fun FinanceCompactHeader(
    onToggleHide: () -> Unit,
    hideBalance: Boolean,
    onOpenSettings: () -> Unit = {},
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .statusBarsPadding()
            .padding(start = 16.dp, end = 16.dp, top = 14.dp, bottom = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        TeddLogo(size = 28.dp)
        Column(modifier = Modifier.weight(1f)) {
            Text(
                "FINANCE",
                color = TeddTokens.InkMuted,
                fontSize = 10.sp,
                fontWeight = FontWeight.Bold,
                letterSpacing = 0.6.sp,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                "Comptes & flux",
                style = TextStyle(
                    color = TeddTokens.Ink,
                    fontSize = 18.sp,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = (-0.3).sp,
                ),
            )
        }
        Box(
            modifier = Modifier
                .size(36.dp)
                .clip(CircleShape)
                .background(
                    if (hideBalance) TeddTokens.LimeSoft else TeddTokens.SurfaceSunken,
                    CircleShape,
                )
                .border(
                    1.dp,
                    if (hideBalance) TeddTokens.LimeStrong else TeddTokens.Line,
                    CircleShape,
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onToggleHide,
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(
                name = if (hideBalance) "eye-off" else "eye",
                size = 14.dp,
                color = if (hideBalance) TeddTokens.LimeStrong else TeddTokens.InkMuted,
            )
        }
        Box(
            modifier = Modifier
                .size(36.dp)
                .clip(CircleShape)
                .background(TeddTokens.SurfaceSunken, CircleShape)
                .border(1.dp, TeddTokens.Line, CircleShape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onOpenSettings,
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "settings", size = 15.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp)
        }
    }
}

// PhoneAccountChips supprimé : le switcher est maintenant intégré dans le hero (FinanceCompactHero)
// et ouvre FinanceAccountSwitcherSheet (bottom sheet plein).

@Composable
internal fun FinanceCompactHero(
    account: Finance_account,
    txns: List<com.tedd.db.Finance_transaction>,
    accountCount: Int,
    onOpenSwitcher: () -> Unit,
    onEdit: () -> Unit,
) {
    val color = parseFinHex(account.color)
    val monthStart = TODAY_ISO.substring(0, 8) + "01"
    val monthEnd = TODAY_ISO.substring(0, 8) + "31"
    val monthTxns = remember(txns, monthStart, monthEnd) {
        txns.filter { it.date in monthStart..monthEnd && it.is_deleted == 0L }
    }
    val income = monthTxns.filter { it.type == "income" && it.account_id == account.id }.sumOf { it.amount }
    val expense = monthTxns.filter { it.type == "expense" && it.account_id == account.id }.sumOf { it.amount }
    val hidden = LocalHideBalance.current

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp)
            .clip(RoundedCornerShape(TeddTokens.RLg))
            // Bloc d'encre : le solde est le chiffre qui compte, sur un aplat sans décor.
            .background(TeddTokens.Ink, RoundedCornerShape(TeddTokens.RLg)),
    ) {
        // Edit affordance (top-right)
        Box(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .padding(12.dp)
                .size(28.dp)
                .clip(CircleShape)
                .background(TeddTokens.OnAction.copy(alpha = 0.18f), CircleShape)
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = onEdit,
                ),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = "edit", size = 13.dp, color = TeddTokens.OnAction, strokeWidth = 1.8.dp)
        }

        Column(modifier = Modifier.padding(18.dp)) {
            // Account switcher chip (embedded in hero)
            Row(
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RFull))
                    .background(TeddTokens.OnAction.copy(alpha = 0.18f), RoundedCornerShape(TeddTokens.RFull))
                    .border(1.dp, TeddTokens.OnAction.copy(alpha = 0.25f), RoundedCornerShape(TeddTokens.RFull))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = onOpenSwitcher,
                    )
                    .padding(start = 4.dp, end = 10.dp, top = 4.dp, bottom = 4.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Box(
                    modifier = Modifier
                        .size(20.dp)
                        .clip(RoundedCornerShape(TeddTokens.RXs))
                        .background(TeddTokens.OnAction.copy(alpha = 0.22f), RoundedCornerShape(TeddTokens.RXs)),
                    contentAlignment = Alignment.Center,
                ) {
                    TeddIcon(name = account.icon, size = 11.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp)
                }
                Text(
                    text = account.name,
                    color = TeddTokens.OnAction,
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Medium,
                    maxLines = 1,
                )
                if (accountCount > 1) {
                    TeddIcon(name = "chevron", size = 10.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp)
                }
            }

            Spacer(modifier = Modifier.height(16.dp))
            Text(
                "Solde disponible",
                color = TeddTokens.OnAction.copy(alpha = 0.85f),
                fontSize = 11.sp,
                fontWeight = FontWeight.Medium,
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = if (hidden) "•••••" else fmtMoney(account.balance, account.currency),
                color = TeddTokens.OnAction,
                fontSize = 34.sp,
                fontWeight = FontWeight.Medium,
                fontFamily = LocalTeddFonts.current.mono,
                letterSpacing = (-0.7).sp,
            )

            AccountThresholdBadges(
                account = account,
                onLight = false,
                modifier = Modifier.padding(top = 10.dp),
            )

            Spacer(modifier = Modifier.height(14.dp))
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.Bottom,
            ) {
                HeroMiniStat(
                    label = "Entrées du mois",
                    value = "+" + fmtMoneyMasked(income, account.currency, hidden, compact = true),
                )
                HeroMiniStat(
                    label = "Sorties du mois",
                    value = "−" + fmtMoneyMasked(expense, account.currency, hidden, compact = true),
                )
                val bank = account.bank
                if (!bank.isNullOrBlank()) {
                    Spacer(modifier = Modifier.weight(1f))
                    Column(horizontalAlignment = Alignment.End) {
                        Text(
                            bank,
                            color = TeddTokens.OnAction.copy(alpha = 0.8f),
                            fontSize = 10.sp,
                        )
                        if (!account.last4.isNullOrBlank()) {
                            Text(
                                text = "••••${account.last4}",
                                color = TeddTokens.OnAction.copy(alpha = 0.9f),
                                fontSize = 12.sp,
                                fontFamily = LocalTeddFonts.current.mono,
                            )
                        }
                    }
                }
            }
        }
    }
}

/** Le compte et son solde sur une ligne, quand l'écran montre autre chose que les transactions. */
@Composable
internal fun FinanceCompactHeroSlim(account: Finance_account, accountCount: Int, onOpenSwitcher: () -> Unit) {
    val hidden = LocalHideBalance.current
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 6.dp)
            .clip(shape)
            .background(TeddTokens.Ink, shape)
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onOpenSwitcher)
            .padding(horizontal = 14.dp, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        TeddIcon(name = account.icon, size = 16.dp, color = TeddTokens.OnAction)
        Text(account.name, color = TeddTokens.OnAction, style = org.teddapp.project.ui.TeddType.Label, maxLines = 1, modifier = Modifier.weight(1f))
        Text(
            text = if (hidden) "•••••" else fmtMoney(account.balance, account.currency),
            color = TeddTokens.OnAction,
            style = org.teddapp.project.ui.TeddType.Amount,
            maxLines = 1,
        )
        if (accountCount > 1) TeddIcon(name = "chevron-down", size = 14.dp, color = TeddTokens.OnAction)
    }
}

@Composable
internal fun HeroMiniStat(label: String, value: String) {
    Column {
        Text(label, color = TeddTokens.OnAction.copy(alpha = 0.8f), fontSize = 10.sp)
        Spacer(modifier = Modifier.height(2.dp))
        Text(
            value,
            color = TeddTokens.OnAction,
            fontSize = 13.sp,
            fontWeight = FontWeight.SemiBold,
            fontFamily = LocalTeddFonts.current.mono,
        )
    }
}

@Composable
internal fun FinanceCompactTabBar(
    tab: FinanceTab,
    onTabChange: (FinanceTab) -> Unit,
    txnCount: Int,
    listsCount: Int,
    assetsCount: Int = 0,
    simulationsCount: Int = 0,
) {
    data class TabSpec(val key: FinanceTab, val label: String, val icon: String, val count: Int)
    val tabs = listOf(
        // Cinq onglets sur un téléphone : « Transactions » ne tient plus, « Flux » dit la même chose.
        TabSpec(FinanceTab.Transactions, "Flux", "list", txnCount),
        TabSpec(FinanceTab.Dashboard, "Tableau", "cards", txnCount),
        TabSpec(FinanceTab.Lists, "Listes", "inbox", listsCount),
        TabSpec(FinanceTab.Patrimoine, "Patrimoine", "home", assetsCount),
        TabSpec(FinanceTab.Simulation, "Simul.", "draw", simulationsCount),
    )
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        tabs.forEach { spec ->
            val on = tab == spec.key
            Column(
                modifier = Modifier
                    .weight(1f)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(
                        if (on) TeddTokens.LimeSoft else TeddTokens.Surface,
                        RoundedCornerShape(TeddTokens.RSm),
                    )
                    .border(
                        1.dp,
                        if (on) TeddTokens.LimeStrong else TeddTokens.Line,
                        RoundedCornerShape(TeddTokens.RSm),
                    )
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = { onTabChange(spec.key) },
                    )
                    .padding(vertical = 8.dp, horizontal = 4.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(2.dp),
            ) {
                TeddIcon(
                    name = spec.icon,
                    size = 14.dp,
                    color = if (on) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                    strokeWidth = if (on) 2.dp else 1.7.dp,
                )
                Text(
                    text = spec.label,
                    color = if (on) TeddTokens.LimeStrong else TeddTokens.InkMuted,
                    fontSize = 11.5.sp,
                    fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                    maxLines = 1,
                )
                Text(
                    text = spec.count.toString(),
                    color = if (on) TeddTokens.LimeStrong.copy(alpha = 0.65f) else TeddTokens.InkMuted,
                    fontSize = 10.sp,
                    fontFamily = LocalTeddFonts.current.mono,
                )
            }
        }
    }
}

@Composable
internal fun FinanceCompactFab(onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .size(56.dp)
            .clip(CircleShape)
            .background(TeddTokens.SignatureGradient, CircleShape)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            ),
        contentAlignment = Alignment.Center,
    ) {
        TeddIcon(name = "plus", size = 22.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp)
    }
}

// Format spécial pour le hero — version non-compact toujours, masquée si hideBalance
@Composable
internal fun moneyM(n: Long, currency: String): String =
    fmtMoneyMasked(n, currency, LocalHideBalance.current, compact = false)

// ──────────────────────────────────────────────────────────────────────
// Bottom sheets — switcher + quick-add
// ──────────────────────────────────────────────────────────────────────

@Composable
internal fun FinanceAccountSwitcherSheet(
    accounts: List<Finance_account>,
    selectedId: String?,
    onSelect: (String) -> Unit,
    onCreate: () -> Unit,
    onDismiss: () -> Unit,
    defaultAccountId: String? = null,
    onToggleDefault: (String) -> Unit = {},
) {
    val hidden = LocalHideBalance.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(start = 18.dp, end = 18.dp, top = 4.dp, bottom = 28.dp),
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        FinanceTotalCard(
            accounts = accounts,
            hideBalance = hidden,
            compact = true,
            modifier = Modifier.padding(bottom = 6.dp),
        )
        Text(
            "Choisir un compte",
            color = TeddTokens.Ink,
            fontSize = 17.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.padding(start = 4.dp, top = 4.dp, bottom = 8.dp),
        )
        accounts.forEach { a ->
            val on = a.id == selectedId
            val estDefaut = a.id == defaultAccountId
            val c = parseFinHex(a.color)
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(TeddTokens.RMd))
                    .background(
                        if (on) c.copy(alpha = 0.08f) else TeddTokens.Surface,
                        RoundedCornerShape(TeddTokens.RMd),
                    )
                    .border(
                        1.dp,
                        if (on) c else TeddTokens.Line,
                        RoundedCornerShape(TeddTokens.RMd),
                    )
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {
                            onSelect(a.id)
                            onDismiss()
                        },
                    )
                    .padding(horizontal = 12.dp, vertical = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(10.dp),
            ) {
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(
                            Brush.linearGradient(listOf(c, c.copy(alpha = 0.66f))),
                            RoundedCornerShape(TeddTokens.RSm),
                        ),
                    contentAlignment = Alignment.Center,
                ) {
                    TeddIcon(name = a.icon, size = 15.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp)
                }
                Column(modifier = Modifier.weight(1f)) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                        Text(
                            text = a.name,
                            color = TeddTokens.Ink,
                            fontSize = 13.5.sp,
                            fontWeight = FontWeight.SemiBold,
                            maxLines = 1,
                        )
                        // C'est sur téléphone que le compte par défaut sert le plus :
                        // on y saisit ses dépenses, et un compte de trop à choisir est
                        // un geste de trop à chaque fois.
                        if (a.id == defaultAccountId) Text("●", color = c, fontSize = 9.sp)
                    }
                    val sub = a.bank?.let { b ->
                        if (!a.last4.isNullOrBlank()) "$b · ****${a.last4}" else b
                    } ?: "Mobile money"
                    Text(
                        text = if (a.id == defaultAccountId) "$sub · par défaut" else sub,
                        color = TeddTokens.InkMuted,
                        fontSize = 11.sp,
                    )
                    if (on) {
                        Text(
                            text = if (estDefaut) "retirer le défaut" else "définir par défaut",
                            color = TeddTokens.InkSubtle,
                            fontSize = 10.5.sp,
                            modifier = Modifier
                                .padding(top = 3.dp)
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                ) { onToggleDefault(a.id) },
                        )
                    }
                }
                Text(
                    text = fmtMoneyMasked(a.balance, a.currency, hidden, compact = true),
                    color = TeddTokens.Ink,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.SemiBold,
                    fontFamily = LocalTeddFonts.current.mono,
                )
            }
        }
        Spacer(modifier = Modifier.height(4.dp))
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.LimeStrong.copy(alpha = 0.4f), RoundedCornerShape(TeddTokens.RMd))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = {
                        onCreate()
                        onDismiss()
                    },
                )
                .padding(vertical = 12.dp, horizontal = 14.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Box(
                modifier = Modifier
                    .size(32.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.SignatureGradient, RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp)
            }
            Text(
                "Nouveau compte",
                color = TeddTokens.LimeStrong,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
    }
}

@Composable
internal fun FinanceQuickAddSheet(
    account: Finance_account,
    categories: List<com.tedd.db.Finance_category>,
    onDismiss: () -> Unit,
    onAdvanced: () -> Unit,
    onSave: (type: String, amount: Long, description: String, catId: String?) -> Unit,
) {
    var type by remember { mutableStateOf("expense") }
    var amount by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    val visibleCats = remember(categories, type) {
        if (type == "transfer") emptyList()
        else categories.filter { it.type == type && it.is_deleted == 0L }
    }
    var catId by remember(type) { mutableStateOf(visibleCats.firstOrNull()?.id) }
    val amountValue = amount.replace(",", ".").toDoubleOrNull()?.toLong() ?: 0L
    val valid = amountValue > 0L
    val currencySym = when (account.currency) {
        "USD" -> "$"
        "EUR" -> "€"
        else -> "F"
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .padding(start = 18.dp, end = 18.dp, top = 4.dp, bottom = 24.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Column(modifier = Modifier.weight(1f).padding(top = 4.dp)) {
                Text(
                    "Nouvelle transaction",
                    color = TeddTokens.Ink,
                    fontSize = 17.sp,
                    fontWeight = FontWeight.SemiBold,
                )
                Text(
                    account.name,
                    color = TeddTokens.InkMuted,
                    fontSize = 11.5.sp,
                )
            }
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = {
                            onAdvanced()
                            onDismiss()
                        },
                    )
                    .padding(horizontal = 8.dp, vertical = 6.dp),
            ) {
                Text(
                    "Avancé →",
                    color = TeddTokens.LimeStrong,
                    fontSize = 11.5.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
        }
        Spacer(modifier = Modifier.height(14.dp))

        // Type segmented
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(3.dp),
            horizontalArrangement = Arrangement.spacedBy(2.dp),
        ) {
            listOf(
                "expense" to "Dépense",
                "income" to "Entrée",
                "transfer" to "Transfert",
            ).forEach { (k, label) ->
                val on = type == k
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .height(32.dp)
                        .clip(RoundedCornerShape(TeddTokens.RXs))
                        .background(
                            if (on) TeddTokens.Surface else Color.Transparent,
                            RoundedCornerShape(TeddTokens.RXs),
                        )
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { type = k },
                        ),
                    contentAlignment = Alignment.Center,
                ) {
                    Text(
                        label,
                        color = if (on) TeddTokens.Ink else TeddTokens.InkMuted,
                        fontSize = 12.5.sp,
                        fontWeight = FontWeight.Medium,
                    )
                }
            }
        }

        Spacer(modifier = Modifier.height(12.dp))

        // Amount — big monospace input
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RMd))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RMd))
                .padding(horizontal = 16.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(modifier = Modifier.weight(1f)) {
                if (amount.isEmpty()) {
                    Text(
                        "0",
                        color = TeddTokens.InkSubtle,
                        fontSize = 32.sp,
                        fontWeight = FontWeight.Medium,
                        fontFamily = LocalTeddFonts.current.mono,
                    )
                }
                BasicTextField(
                    value = amount,
                    onValueChange = { newValue ->
                        if (newValue.all { ch -> ch.isDigit() || ch == ',' || ch == '.' }) {
                            amount = newValue
                        }
                    },
                    singleLine = true,
                    textStyle = TextStyle(
                        color = TeddTokens.Ink,
                        fontSize = 32.sp,
                        fontWeight = FontWeight.Medium,
                        fontFamily = LocalTeddFonts.current.mono,
                        letterSpacing = (-0.6).sp,
                    ),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                )
            }
            Text(
                currencySym,
                color = TeddTokens.InkMuted,
                fontSize = 16.sp,
                fontWeight = FontWeight.Medium,
            )
        }

        Spacer(modifier = Modifier.height(10.dp))

        // Description
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(TeddTokens.RSm))
                .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                .padding(horizontal = 14.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(modifier = Modifier.weight(1f)) {
                if (description.isEmpty()) {
                    Text(
                        "Description (ex. Marché)",
                        color = TeddTokens.InkSubtle,
                        fontSize = 13.sp,
                    )
                }
                BasicTextField(
                    value = description,
                    onValueChange = { description = it },
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 13.sp),
                    cursorBrush = SolidColor(TeddTokens.LimeStrong),
                )
            }
        }

        // Categories pills (only for expense / income)
        if (type != "transfer" && visibleCats.isNotEmpty()) {
            Spacer(modifier = Modifier.height(12.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .horizontalScroll(rememberScrollState()),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                visibleCats.forEach { c ->
                    val cc = parseFinHex(c.color)
                    val on = c.id == catId
                    Row(
                        modifier = Modifier
                            .clip(RoundedCornerShape(TeddTokens.RFull))
                            .background(
                                if (on) cc.copy(alpha = 0.12f) else TeddTokens.Surface,
                                RoundedCornerShape(TeddTokens.RFull),
                            )
                            .border(
                                1.dp,
                                if (on) cc else TeddTokens.Line,
                                RoundedCornerShape(TeddTokens.RFull),
                            )
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = { catId = c.id },
                            )
                            .padding(horizontal = 10.dp, vertical = 6.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(5.dp),
                    ) {
                        TeddIcon(
                            name = c.icon,
                            size = 11.dp,
                            color = if (on) cc else TeddTokens.InkMuted,
                            strokeWidth = 2.dp,
                        )
                        Text(
                            c.name,
                            color = if (on) cc else TeddTokens.Ink,
                            fontSize = 12.sp,
                            fontWeight = if (on) FontWeight.SemiBold else FontWeight.Medium,
                        )
                    }
                }
            }
        }

        Spacer(modifier = Modifier.height(18.dp))

        // Submit
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(48.dp)
                .clip(RoundedCornerShape(TeddTokens.RMd))
                .background(
                    if (valid) TeddTokens.SignatureGradient else SolidColor(TeddTokens.Line),
                    RoundedCornerShape(TeddTokens.RMd),
                )
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    enabled = valid,
                    onClick = {
                        onSave(
                            type,
                            amountValue,
                            description,
                            if (type == "transfer") null else catId,
                        )
                        onDismiss()
                    },
                ),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                "Enregistrer",
                color = TeddTokens.OnAction,
                fontSize = 15.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
    }
}
